/**
 * Contextual help : HTML of the panel content, built from the manifest and the page files.
 *
 * Pure string building (no DOM) so it is tested by jest. The documentation HTML comes from
 * tools/doc-build/publish-help.js, sanitized at build time (whitelist, no id, doc links as
 * data-help-ref without href) : it is inserted as is. Everything coming from the manifest
 * (titles) and from the translations is escaped here.
 *
 * Stable selectors (E2E, spec § 5.4) : [data-help-section="<id>"] on every section block,
 * [data-help-page="<page id>"] on every page block, [data-help-ref] on every doc link,
 * [data-help-action] on every command, [data-help-screen] on the "other screens" links.
 */
const {isUserSection} = require("./helpIndex") ;

function escapeHtml(text){
    return String(text === undefined || text === null ? "" : text)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;") ;
}

function sectionBlock(index, pageSection){
    const section = index.sections[pageSection.i] ;
    const id = section ? section.id : "" ;
    return '<div class="help-section" data-help-section="'+escapeHtml(id)+'">'+pageSection.html+'</div>' ;
}

/**
 * Title of a page, a link to read it entirely
 */
function pageTitle(page, tag){
    return '<'+tag+' class="help-page-title">'
        +'<a data-help-ref="'+escapeHtml(page.id)+'">'+escapeHtml(page.title)+'</a></'+tag+'>' ;
}

/**
 * Sections of a page file, by section index
 */
function sectionsByIdx(pageData){
    const map = new Map() ;
    ((pageData && pageData.sections) || []).forEach((s) => map.set(s.i, s)) ;
    return map ;
}

/**
 * "Screen" mode : the sections documenting the current screen (decision D2)
 *
 * @param {object} index the manifest
 * @param {object} help helpIndex.screenHelp() result
 * @param {Map} pagesData page index → page file content (helpIndex groups' pages, loaded)
 * @param {function} tr translation function (key → text)
 * @return {string} HTML
 */
function screenHtml(index, help, pagesData, tr){
    const multiple = help.groups.length > 1 ;
    let html = "" ;
    help.groups.forEach((group, n) => {
        const page = index.pages[group.page] ;
        const data = pagesData.get(group.page) || {} ;
        const byIdx = sectionsByIdx(data) ;
        //screen mode : the intro without its page summary (links to the sections of the other
        //screens of the page, up to 25 lines), published as introScreen when it has one (L1.5b)
        const intro = data.introScreen !== undefined ? data.introScreen : data.intro ;
        let body = intro ? '<div class="help-intro">'+intro+'</div>' : "" ;
        group.user.forEach((i) => { if(byIdx.has(i)){ body += sectionBlock(index, byIdx.get(i)) ; } }) ;
        const consultant = group.consultant.filter((i) => byIdx.has(i)).map((i) => sectionBlock(index, byIdx.get(i))).join("") ;
        if(consultant){
            if(help.consultantOpen){
                body += consultant ;
            }else{
                body += '<details class="help-consultant" data-help-consultant>'
                    +'<summary>'+escapeHtml(tr("help.consultant"))+'</summary>'+consultant+'</details>' ;
            }
        }
        if(multiple){
            html += '<details class="help-page" data-help-page="'+escapeHtml(page.id)+'"'+(n === 0 ? " open" : "")+'>'
                +'<summary>'+escapeHtml(page.title)+'</summary>'
                +'<div class="help-page-body"><p class="help-read-page"><a data-help-ref="'+escapeHtml(page.id)+'">'
                +escapeHtml(tr("help.readPage"))+'</a></p>'+body+'</div></details>' ;
        }else{
            html += '<div class="help-page" data-help-page="'+escapeHtml(page.id)+'">'+pageTitle(page, "h1")+body+'</div>' ;
        }
    }) ;
    if(help.others.length){
        html += '<div class="help-others"><h2 class="help-block-title">'+escapeHtml(tr("help.otherScreens"))+'</h2><ul>' ;
        help.others.forEach((other) => {
            html += '<li><a data-help-screen="'+escapeHtml(other.code)+'">'+escapeHtml(otherLabel(index, other))+'</a></li>' ;
        }) ;
        html += '</ul></div>' ;
    }
    return html ;
}

/**
 * Label of another open screen : the titles of the pages documenting it
 */
function otherLabel(index, entry){
    const titles = [] ;
    entry.sections.forEach((i) => {
        const section = index.sections[i] ;
        const title = section && index.pages[section.p] && index.pages[section.p].title ;
        if(title && titles.indexOf(title) === -1){ titles.push(title) ; }
    }) ;
    return titles.join(" · ") || entry.code ;
}

/**
 * "Read" mode : a whole page, consultant-only sections folded at their place (D2)
 *
 * @param {object} index the manifest
 * @param {number} pageIdx index of the page in the manifest
 * @param {object} pageData the page file content
 * @param {function} tr translation function
 * @return {string} HTML
 */
function readHtml(index, pageIdx, pageData, tr){
    const page = index.pages[pageIdx] ;
    let html = '<div class="help-page" data-help-page="'+escapeHtml(page.id)+'">'
        +'<h1 class="help-page-title" data-help-anchor="'+escapeHtml(pageData.anchor || "")+'">'+escapeHtml(page.title)+'</h1>' ;
    if(pageData.intro){ html += '<div class="help-intro">'+pageData.intro+'</div>' ; }
    (pageData.sections || []).forEach((s) => {
        const section = index.sections[s.i] ;
        if(!section || isUserSection(index, s.i)){
            html += sectionBlock(index, s) ;
        }else{
            html += '<details class="help-consultant" data-help-consultant>'
                +'<summary>'+escapeHtml(tr("help.consultant")+" — "+section.t)+'</summary>'
                +sectionBlock(index, s)+'</details>' ;
        }
    }) ;
    return html+'</div>' ;
}

/**
 * "Table of contents" mode : the README tree, the full documentation
 *
 * @param {object} index the manifest
 * @param {function} tr translation function
 * @return {string} HTML
 */
function tocHtml(index, tr){
    function nodes(children, depth){
        let html = "" ;
        let list = "" ;
        (children || []).forEach((node) => {
            if(node.page !== undefined && index.pages[node.page]){
                list += '<li><a data-help-ref="'+escapeHtml(index.pages[node.page].id)+'">'+escapeHtml(node.title)+'</a></li>' ;
            }else if(node.children){
                if(list){ html += '<ul>'+list+'</ul>' ; list = "" ; }
                if(depth === 0){
                    html += '<h2 class="help-block-title">'+escapeHtml(node.title)+'</h2>'+nodes(node.children, depth+1) ;
                }else{
                    html += '<details class="help-toc-group"><summary>'+escapeHtml(node.title)+'</summary>'
                        +nodes(node.children, depth+1)+'</details>' ;
                }
            }
        }) ;
        return html+(list ? '<ul>'+list+'</ul>' : "") ;
    }
    return '<div class="help-toc"><h1 class="help-page-title">'+escapeHtml(tr("help.toc"))+'</h1>'+nodes(index.toc, 0)+'</div>' ;
}

/**
 * Search results : section title + page title
 *
 * @param {object} index the manifest
 * @param {number[]} results section indexes (helpIndex.searchTitles)
 * @param {function} tr translation function
 * @return {string} HTML
 */
function searchHtml(index, results, tr){
    if(!results.length){
        return '<p class="help-message text-muted">'+escapeHtml(tr("help.noResult"))+'</p>' ;
    }
    return '<ul class="help-results">'+results.map((i) => {
        const section = index.sections[i] ;
        const page = index.pages[section.p] ;
        return '<li><a data-help-ref="'+escapeHtml(section.id)+'">'+escapeHtml(section.t)+'</a>'
            +'<small class="text-muted">'+escapeHtml(page ? page.title : "")
            +(section.a === "c" ? " · "+escapeHtml(tr("help.consultantShort")) : "")+'</small></li>' ;
    }).join("")+'</ul>' ;
}

module.exports = {
    escapeHtml,
    screenHtml,
    readHtml,
    tocHtml,
    searchHtml,
    otherLabel,
} ;
